/**
 * Root-level error boundary.
 *
 * Why this exists: when any descendant component throws during render,
 * React unmounts the entire tree. The app's `#root:empty::before` CSS
 * splash then takes over and looks like an indefinite "loading" screen
 * with no error feedback (the bug reported in iter-357). This boundary
 * catches those crashes and shows an actionable error UI instead.
 *
 * Hook: AppErrorBoundary should wrap <AppRoutes /> in App.js.
 */
import React from 'react';

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Surface to console so support / browser devtools can capture it.
    // eslint-disable-next-line no-console
    console.error('[AppErrorBoundary] render crash:', error, info);
    // Best-effort telemetry — if the global window.onerror hook is wired
    // up to a logger, it will already see this; we just nudge.
    try {
      if (typeof window !== 'undefined' && window.__fwm_logRenderError) {
        window.__fwm_logRenderError({ error: String(error), stack: error?.stack, info });
      }
    } catch (_e) { /* ignore */ }
  }

  reset = () => {
    this.setState({ error: null, info: null });
  };

  reloadHard = () => {
    try {
      // Bust any service worker cache that might be serving a stale bundle
      if ('caches' in window) {
        caches.keys().then(keys => keys.forEach(k => caches.delete(k)));
      }
    } catch (_e) { /* ignore */ }
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || 'Unknown error');
    return (
      <div
        data-testid="app-error-boundary"
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          background: '#f8fafc',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        }}
      >
        <div style={{ maxWidth: 520, width: '100%', background: '#ffffff',
                       border: '1px solid #e2e8f0', borderRadius: 12, padding: 24,
                       boxShadow: '0 4px 12px rgba(15,23,42,0.04)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
            <div style={{ width: 36, height: 36, borderRadius: 999, background: '#fee2e2',
                           display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#b91c1c', fontSize: 20 }}>!</div>
            <h1 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>Something went wrong</h1>
          </div>
          <p style={{ color: '#475569', fontSize: 14, lineHeight: 1.5, margin: '8px 0 12px' }}>
            The page failed to load. This usually means the data for the item you opened is malformed, or the app bundle is stale.
            Try the actions below — your work is safe.
          </p>
          <pre style={{ background: '#f1f5f9', color: '#0f172a', fontSize: 11, padding: 10,
                          borderRadius: 6, overflow: 'auto', maxHeight: 140,
                          whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' }}>
            <button
              data-testid="app-error-retry"
              onClick={this.reset}
              style={btnPrimary}
            >Try again</button>
            <button
              data-testid="app-error-back"
              onClick={() => { this.reset(); window.history.back(); }}
              style={btnSecondary}
            >Go back</button>
            <button
              data-testid="app-error-reload"
              onClick={this.reloadHard}
              style={btnSecondary}
            >Reload app</button>
          </div>
          <p style={{ marginTop: 14, fontSize: 11, color: '#94a3b8' }}>
            If this keeps happening, screenshot this dialog and share with support.
          </p>
        </div>
      </div>
    );
  }
}

const btnPrimary = {
  padding: '8px 14px', borderRadius: 6, border: 'none',
  background: '#4f46e5', color: '#fff', fontSize: 13, cursor: 'pointer', fontWeight: 500,
};
const btnSecondary = {
  padding: '8px 14px', borderRadius: 6, border: '1px solid #cbd5e1',
  background: '#ffffff', color: '#0f172a', fontSize: 13, cursor: 'pointer',
};

export default AppErrorBoundary;
